"use client";

/**
 * Holds the signed-in admin's pending-work counts: one fetch per sign-in, shared
 * by the navbar dot, the admin tiles and the Hub tab counts. The provider owns the
 * token; consumers call `useAdminPendingWork()` and `refresh()` after a mutation.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AdminPendingWorkContext,
  fetchAdminPendingWork,
  type AdminPendingWork,
} from "../../api/adminPendingWork";
import { useAuth, userHasAnyAdminAccessState } from "../../util/auth";
import { logger } from "../../util/logger";

type PendingAuth = {
  user?: { uid?: string; email?: string } | false | null;
  getAccessToken?: () => Promise<string | null>;
};

export default function AdminPendingWorkProvider({ children }: { children: ReactNode }) {
  const auth = useAuth() as PendingAuth;
  const authUser = auth?.user ? auth.user : null;
  const enabled = userHasAnyAdminAccessState(authUser) === true;
  const userKey = authUser ? authUser.uid ?? authUser.email ?? "user" : "";

  // getAccessToken is a new function each render; read it through a ref so it
  // never retriggers the fetch.
  const getTokenRef = useRef(auth?.getAccessToken);
  getTokenRef.current = auth?.getAccessToken;

  const [work, setWork] = useState<AdminPendingWork | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async (): Promise<AdminPendingWork | null> => {
    const token = await getTokenRef.current?.();
    if (!token) {
      return null;
    }
    return fetchAdminPendingWork(token);
  }, []);

  useEffect(() => {
    const id = ++requestId.current;
    if (!enabled) {
      setWork(null);
      return undefined;
    }
    load()
      .then((result) => {
        if (id === requestId.current) {
          setWork(result);
        }
      })
      .catch((error) => {
        logger("Admin pending-work fetch failed.", error);
        if (id === requestId.current) {
          setWork(null);
        }
      });
    return () => {
      // Invalidate on unmount or when the sign-in changes.
      requestId.current += 1;
    };
  }, [enabled, userKey, load]);

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }
    const id = ++requestId.current;
    try {
      const result = await load();
      if (id === requestId.current && result) {
        setWork(result);
      }
    } catch (error) {
      // Keep the last known counts; the next refresh or page load corrects them.
      logger("Admin pending-work refresh failed.", error);
    }
  }, [enabled, load]);

  const value = useMemo(() => ({ work, refresh }), [work, refresh]);

  return (
    <AdminPendingWorkContext.Provider value={value}>{children}</AdminPendingWorkContext.Provider>
  );
}
